/**
 * useAccountBurnRate — how fast is this organization actually spending?
 *
 * WHY (Miles, 2026-09-11, after INCIDENT 2026-09-10). The top-up ladder was a
 * fixed $100 / $250 / $500 / $1,000 with $500 pre-selected, and auto top-up
 * defaulted to $500 whenever the balance fell below $25. His ruling: the
 * suggested options should "center around timeline moving aggregate strategic
 * averages based on usage (few days) (one week) (2 weeks) (1 month), making
 * their options calculated, easy and personalized just for the organization's
 * usage".
 *
 * SOURCE. `accounts/{accountId}/account_usage/{YYYY-MM-DD}` — the per-day
 * `amountUsd` rollups the debit chokepoint already writes. Deliberately NOT
 * the raw expense ledger: one account wrote 625 expense rows in eight days,
 * so a 30-day window there is thousands of document reads every time the modal
 * opens. This is ~30.
 *
 * SCOPE. The ORGANIZATION's usage, per the ruling — read through the account
 * the user is acting in (`users/{uid}/account_membership/main`), which is the
 * same pointer the wallet display resolver uses. Personal-plane users simply
 * have no account and fall back to the flat ladder.
 *
 * The arithmetic lives in ../utils/burnRate, a twin of the server's SSOT that
 * a parity test pins. This hook only fetches and gap-fills.
 */
import { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore';
import db from '../utils/firestore';
import { computeBurnRate, BurnRate } from '../utils/burnRate';

/** Days of history to pull. One more than the medium window, for gap-filling. */
const WINDOW_DAYS = 31;

export interface UseAccountBurnRateResult {
  burn: BurnRate | null;
  isLoading: boolean;
}

/** `YYYY-MM-DD` for `n` days before today, UTC — the key shape of the rollups. */
function utcDayKey(daysAgo: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

export function useAccountBurnRate(uid: string | null | undefined): UseAccountBurnRateResult {
  const [burn, setBurn] = useState<BurnRate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(uid));

  useEffect(() => {
    let alive = true;
    if (!uid) {
      setBurn(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    (async () => {
      try {
        const memberSnap = await getDoc(doc(db, `users/${uid}/account_membership/main`));
        const member = memberSnap.exists() ? (memberSnap.data() as Record<string, unknown>) : null;
        const accountId =
          (typeof member?.accountId === 'string' && member.accountId) ||
          (typeof (member?.explicitChoice as any)?.accountId === 'string'
            ? ((member!.explicitChoice as any).accountId as string)
            : '');
        if (!accountId) {
          // Personal plane: no organization pace to personalise against.
          if (alive) { setBurn(null); setIsLoading(false); }
          return;
        }

        // Bounded by document id, so this is a key range — no index needed and
        // no rollup outside the window is ever read.
        const keys: string[] = [];
        for (let i = 0; i < WINDOW_DAYS; i += 1) keys.push(utcDayKey(i));
        const oldest = keys[keys.length - 1];
        const newest = keys[0];

        const snap = await getDocs(query(
          collection(db, `accounts/${accountId}/account_usage`),
          where(documentId(), '>=', oldest),
          where(documentId(), '<=', newest),
        ));

        const byDay: Record<string, number> = {};
        snap.forEach((d) => {
          // The collection also holds MONTHLY rollups keyed `YYYY-MM`; those
          // sort inside the same range and would each read as a colossal day.
          if (!/^\d{4}-\d{2}-\d{2}$/.test(d.id)) return;
          const usd = (d.data() as Record<string, unknown>).amountUsd;
          if (typeof usd === 'number' && Number.isFinite(usd) && usd >= 0) byDay[d.id] = usd;
        });

        // Gap-fill: a day with no spend is a real zero, not missing data.
        // Dropping it would overstate the pace of anyone who works weekdays.
        const days = keys
          .slice()
          .reverse()
          .map((day) => ({ day, usd: byDay[day] ?? 0 }));

        if (alive) { setBurn(computeBurnRate(days)); setIsLoading(false); }
      } catch {
        // Never block a purchase on this: no burn simply means the flat
        // fallback ladder, which is what every user saw before today.
        if (alive) { setBurn(null); setIsLoading(false); }
      }
    })();

    return () => { alive = false; };
  }, [uid]);

  return { burn, isLoading };
}
